import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import Lenis from 'lenis';
import 'lenis/dist/lenis.css';
import { gsap, ScrollTrigger, setupGsap, prefersReducedMotion } from './gsapSetup';

const SmoothScrollContext = createContext(null);

export const useSmoothScroll = () => useContext(SmoothScrollContext);

const isTouchOrNarrow = () => {
  if (typeof window === 'undefined') return true;
  const narrow = window.matchMedia('(max-width: 767px)').matches;
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  return narrow || (coarse && touch && narrow);
};

export const SmoothScroll = ({ children }) => {
  const lenisRef = useRef(null);
  const [lenis, setLenis] = useState(null);

  useEffect(() => {
    setupGsap();

    // Native scroll on phones/tablets — Lenis fights touch inertia
    if (prefersReducedMotion() || isTouchOrNarrow()) {
      document.documentElement.classList.remove('lenis', 'lenis-smooth');
      setLenis(null);
      return undefined;
    }

    const instance = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      syncTouch: false,
      touchMultiplier: 1.2,
    });

    lenisRef.current = instance;
    setLenis(instance);
    window.__lenis = instance;

    instance.on('scroll', ScrollTrigger.update);

    const tickerCallback = (time) => {
      instance.raf(time * 1000);
    };

    gsap.ticker.add(tickerCallback);
    gsap.ticker.lagSmoothing(0);

    const onResize = () => {
      if (isTouchOrNarrow()) {
        instance.destroy();
        lenisRef.current = null;
        setLenis(null);
        document.documentElement.classList.remove('lenis', 'lenis-smooth');
        gsap.ticker.remove(tickerCallback);
        ScrollTrigger.refresh();
        return;
      }
      ScrollTrigger.refresh();
    };

    window.addEventListener('resize', onResize);
    document.documentElement.classList.add('lenis', 'lenis-smooth');

    return () => {
      window.removeEventListener('resize', onResize);
      gsap.ticker.remove(tickerCallback);
      instance.destroy();
      document.documentElement.classList.remove('lenis', 'lenis-smooth');
      lenisRef.current = null;
      setLenis(null);
    };
  }, []);

  const scrollTo = (target, options = {}) => {
    const el =
      typeof target === 'string' ? document.querySelector(target) : target;
    if (!el) return;

    if (lenisRef.current) {
      lenisRef.current.scrollTo(el, {
        offset: options.offset ?? -8,
        duration: options.duration ?? 1.1,
        ...options,
      });
      return;
    }

    el.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  return (
    <SmoothScrollContext.Provider value={{ lenis, scrollTo }}>
      {children}
    </SmoothScrollContext.Provider>
  );
};

export default SmoothScroll;
